import { createEngine } from "./engine/EngineBootstrap";
import { CanvasManager } from "./engine/CanvasManager";
import { showWebGL2FallbackNotice } from "./ui/UnsupportedBrowserOverlay";
import { SceneRoot } from "./scene/SceneRoot";
import { SceneInstrumentation } from "@babylonjs/core";

async function main(): Promise<void> {
  const canvas = document.getElementById("renderCanvas") as HTMLCanvasElement | null;
  if (!canvas) throw new Error("renderCanvas element not found");

  const { engine, backend } = await createEngine(canvas);
  console.info(`[Castaway] renderer backend: ${backend}`);
  if (backend === "webgl2") showWebGL2FallbackNotice();

  new CanvasManager(canvas, engine);

  const sceneRoot = new SceneRoot(engine, canvas);

  if (import.meta.env.DEV) {
    // Dev-only debug surface (dead-code-eliminated from production builds
    // via the import.meta.env.DEV guard) — lets vignettes be force-triggered
    // from the browser console for verification, per the plan's own
    // suggested approach: `__castawayDebug.forceVignette("fishing")`.
    // Draw-call instrumentation for the Phase 4 perf audit (and any future
    // one) — Babylon's engine doesn't expose a simple public draw-call
    // count otherwise; SceneInstrumentation's PerfCounter is the correct
    // way to get one, not the internal-only counters on the engine object.
    const instrumentation = new SceneInstrumentation(sceneRoot.scene);

    (window as unknown as { __castawayDebug: unknown }).__castawayDebug = {
      sceneRoot,
      instrumentation,
      forceVignette: (id: string) => sceneRoot.forceVignette(id),
      listVignetteIds: () => sceneRoot.sceneDirector.listIds(),
    };
    console.info(
      '[Castaway] Debug mode: try __castawayDebug.forceVignette("fishing") in the console. Ids:',
      sceneRoot.sceneDirector.listIds()
    );
  }

  let lastFrameTime = performance.now();
  engine.runRenderLoop(() => {
    const now = performance.now();
    const deltaMs = now - lastFrameTime;
    lastFrameTime = now;

    sceneRoot.update(deltaMs);
    sceneRoot.render();
  });

  document.addEventListener("fullscreenchange", () => engine.resize());
}

main().catch((err) => {
  console.error("[Castaway] fatal init error:", err);
  document.body.innerHTML =
    '<div style="color:#e8edf5;font:14px system-ui,sans-serif;padding:24px;">' +
    "Failed to start the renderer. See console for details." +
    "</div>";
});
